import "../../styles/gradient-button.min.css"
import { useEffect, useState, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useQuery } from "@tanstack/react-query"
import { Box, useMediaQuery, Fade, Skeleton } from "@mui/material"
import { setViewByLabel, setViewBySearchTerm } from "state"
import Navbar from "views/navbar"
import LabelsDrawer from "components/LabelsDrawer"
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import VocabRow from "components/VocabRow"
import VocabBox from "components/VocabBox"
import apiUrl from "config/api"
import MobileFooterNavigation from "../widgets/MobileFooterNavigation"
import DesktopFooter from "../widgets/DesktopFooter"

const AllPage = () => {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px) and (max-height:2160px)")
  const dispatch = useDispatch()
  const viewByLabel = useSelector((state) => state.viewByLabel)
  const viewBySearchTerm = useSelector((state) => state.viewBySearchTerm)

  const isLandscape = window.matchMedia("(orientation: landscape)").matches;

  const token = useSelector((state) => state.token)
  const { _id } = useSelector((state) => state.user)
  const refreshKey = useSelector((state) => state.refreshKey)
  const [fadeIn, setFadeIn] = useState(false)

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 100,
    })
  );

  const getAllVocabs = () => {
    return fetch(`${apiUrl}/vocabs/${_id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        return data.sort((a, b) => a.pinyin.localeCompare(b.pinyin));
      });
  };

  const { data, isLoading } = useQuery(["allVocabsData"], getAllVocabs, {
    keepPreviousData: true,
    staleTime: 1000,
  });

  useEffect(() => {
    if (viewByLabel !== "") {
      dispatch(setViewByLabel(""))
    }
    if (viewBySearchTerm !== "") {
      dispatch(setViewBySearchTerm(""))
    }
  }, [viewByLabel, viewBySearchTerm, dispatch])

  useEffect(() => {
    setFadeIn(true)
  }, [])

  return (
    <Box>
      <Navbar />

      <Fade in={fadeIn} timeout={400}>
      <Box>

      {/* ----- Page Body ----- */}

      <LabelsDrawer />

      <Box
        width="100%"
        padding={isLandscape ? "0.25rem 4%" : "0.25rem 4%"}
        display={isNonMobileScreens ? "flex" : "block"}
        gap="0.5rem"
        justifyContent="center"
      >

        {/* ----- Mid Page Column ----- */}

        <Box
          flexBasis={isNonMobileScreens ? "64%" : undefined}
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          zIndex={10}
        >

          {isLoading ? (
            <VocabBox>
              <div style={{ width: "100%", height: "80vh", overflow: "hidden" }}>
                {/* Mirror the virtualized list: 100px rows (CellMeasurerCache default) with RowBox padding */}
                {Array.from({ length: 12 }).map((_, index) => (
                  <Box key={index} sx={{ height: 100, padding: "0.5rem 1rem", boxSizing: "border-box" }}>
                    <Skeleton variant="rounded" width="100%" height="100%" />
                  </Box>
                ))}
              </div>
            </VocabBox>
          ) : (data?.length > 0) ? (
            <VocabBox sx={{ border: data?.length < 1 && "none" }} key={refreshKey}>
              <div style={{ width: "100%", height: "80vh" }}>
                <AutoSizer>
                  {({ width, height }) => (
                    <List
                      width={width}
                      height={height}
                      rowHeight={cache.current.rowHeight}
                      deferredMeasurementCache={cache.current}
                      rowCount={data?.length}
                      rowRenderer={({ key, index, style, parent }) => {
                        const vocab = data[index]
                        return (
                          <CellMeasurer
                            key={key}
                            cache={cache.current}
                            parent={parent}
                            columnIndex={0}
                            rowIndex={index}
                          >
                            <div style={style}>
                              <VocabRow
                                key={vocab._id}
                                id={vocab._id}
                                text={vocab.text}
                                pinyin={vocab.pinyin}
                                difficulty={vocab.difficulty}
                                definition={vocab.definition}
                                similar={vocab.similar}
                                label={vocab.label}
                                expression={vocab.expression}
                                sentence={vocab.sentence}
                                pinned={vocab.pinned}
                              />
                            </div>
                          </CellMeasurer>
                        );
                      }}
                    />
                  )}
                </AutoSizer>
              </div>
            </VocabBox>
          ) : null}

        </Box>
        {isNonMobileScreens && <DesktopFooter isHome />}
        {!isNonMobileScreens && <MobileFooterNavigation isHome />}
      </Box>
      </Box>
      </Fade>
    </Box>
  )
}

export default AllPage